import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Flex, Radio, Text, Tooltip } from '@bonusly/design-system';
import { useCurrentCompany } from '@bonusly/who-am-i';

import { Award } from '../../../../../../types/bonus';
import { AmountChip } from './AmountChip';
import { useTemplatePreview } from './hooks/useTemplatePreview';

interface OldAwardOptionProps {
	award: Award;
	group: string;
	isChecked: boolean;
	onClick: VoidFunction;
}

const getAwardOptionProps = ({ award, ...rest }: OldAwardOptionProps) => {
	const isInfinite = award.giver?.infinite_budget;
	const isDisabled = !isInfinite && !award.giving_balance;
	const titleColor = isDisabled ? 'gray.500' : rest.isChecked ? 'blue.500' : 'gray.900';

	return {
		award,
		hasTemplate: !!award.reason,
		isDisabled,
		shouldDisplayBudget: !isInfinite,
		titleColor,
		...rest,
	};
};

export const OldAwardOption: FC<OldAwardOptionProps> = (props) => {
	const { t } = useTranslation('recognition');
	const company = useCurrentCompany();

	const { award, isChecked, hasTemplate, isDisabled, onClick, shouldDisplayBudget, titleColor } = getAwardOptionProps(props);

	const template = useTemplatePreview(award);

	return (
		<Flex
			as="label"
			borderBottomColor="gray.100"
			borderBottomStyle="solid"
			borderBottomWidth="1px"
			cursor="pointer"
			px={5}
			py={4}
			w="full"
			{...(isDisabled && {
				bg: 'gray.25',
			})}
		>
			<Box>
				<Radio isChecked={isChecked} isDisabled={isDisabled} onChange={onClick} mt={1.5} value={award.id} />
			</Box>

			<Box flexGrow={1} ml={4}>
				<Flex justifyContent="space-between" mb={1}>
					<Text color={titleColor} fontSize="xl" fontWeight="semibold" lineHeight={8} noOfLines={1}>
						{award.giver.first_name}
					</Text>

					{shouldDisplayBudget && (
						// The tooltip is enabled if the *checkbox* is disabled, which is what isDisabled denotes
						<Tooltip
							isDisabled={!isDisabled}
							label={t(`awards.give_modal.no_budget_${award.budget_period}`, {
								currency: company.budget.plural,
							})}
						>
							<AmountChip amount={award.giving_balance} data-testid="amount-chip" isDisabled={isDisabled} />
						</Tooltip>
					)}
				</Flex>

				<Box>
					{hasTemplate && (
						<Text color={isDisabled ? 'gray.500' : 'gray.700'} fontSize="lg" fontWeight="normal" lineHeight={7} mb={1} noOfLines={1}>
							{t('awards.give_modal.award_template', {
								template,
								interpolation: {
									escapeValue: false,
								},
							})}
						</Text>
					)}

					{shouldDisplayBudget && (
						<Text color="gray.500" fontSize="md" fontWeight="normal" lineHeight={6}>
							{t(`awards.give_modal.allowance_per`, {
								amount: award.allowance,
								context: award.budget_period,
								defaultValue: '',
								formatParams: {
									amount: company.budget.format,
								},
							})}
						</Text>
					)}
				</Box>
			</Box>
		</Flex>
	);
};
